import { TreeNode } from './types';

/**
 * Permissions Tree Structure
 *
 * This defines the hierarchical permissions structure that maps to Better Auth roles.
 * Each permission ID corresponds to one or more Better Auth roles.
 */

// Mapping from permission IDs to Better Auth role names
// UPDATED: 2025-12-04 - Renamed to match menu structure
export const PERMISSION_TO_ROLE_MAP: Record<string, string> = {
  // Admin - has all permissions
  admin: 'admin',

  // Valuations section
  'valuations.dashboard': 'dashboard',
  'valuations.reports': 'reports',
  'valuations.methods': 'methods',
  'valuations.valuations.sales': 'valuations_sales',
  'valuations.valuations.transfer': 'valuations_transfer',
  'valuations.valuations.marketing': 'valuations_marketing',
  'valuations.valuations.brand': 'valuations_brand',

  // Sponsorships section
  'sponsorships.sponsors': 'sponsors',
  'sponsorships.packages': 'packages',
  'sponsorships.inventory': 'inventory',
  'sponsorships.proposals': 'proposals',
  'sponsorships.analytics': 'analytics',

  // Platform section
  'platform.users': 'users',
  'platform.organisation.general': 'organisation',
  'platform.organisation.sso': 'sso',
  'platform.organisation.advanced': 'audit_log',
  'platform.settings': 'app_settings',
  'platform.documents': 'documents',
  'platform.billing': 'billing',
  'platform.plugins': 'plugins',
  'platform.api': 'api',

  // Approver
  approver: 'approver',
};

// Reverse mapping: Better Auth role to permission IDs
export const ROLE_TO_PERMISSION_MAP: Record<string, string[]> = Object.entries(
  PERMISSION_TO_ROLE_MAP
).reduce(
  (acc, [permissionId, role]) => {
    if (!acc[role]) {
      acc[role] = [];
    }
    acc[role].push(permissionId);
    return acc;
  },
  {} as Record<string, string[]>
);

/**
 * Application permissions tree structure
 * This is the source of truth for the permissions hierarchy
 */
export const APPLICATION_PERMISSIONS: TreeNode[] = [
  {
    id: 'admin',
    label: 'Admin',
  },
  {
    id: 'valuations',
    label: 'Valuations',
    children: [
      { id: 'valuations.dashboard', label: 'Dashboard' },
      {
        id: 'valuations.valuations',
        label: 'Valuations',
        children: [
          { id: 'valuations.valuations.sales', label: 'Edit Sales Value' },
          { id: 'valuations.valuations.transfer', label: 'Edit Transfer Value' },
          { id: 'valuations.valuations.marketing', label: 'Edit Marketing Value' },
          { id: 'valuations.valuations.brand', label: 'Edit Brand Value' },
        ],
      },
      { id: 'valuations.reports', label: 'Reports' },
      { id: 'valuations.methods', label: 'Methods' },
    ],
  },
  {
    id: 'sponsorships',
    label: 'Sponsorships',
    children: [
      { id: 'sponsorships.sponsors', label: 'Sponsors' },
      { id: 'sponsorships.packages', label: 'Packages' },
      { id: 'sponsorships.inventory', label: 'Inventory' },
      { id: 'sponsorships.proposals', label: 'Proposals' },
      { id: 'sponsorships.analytics', label: 'Analytics' },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    children: [
      { id: 'platform.users', label: 'Users' },
      {
        id: 'platform.organisation',
        label: 'Organisation',
        children: [
          { id: 'platform.organisation.general', label: 'General' },
          { id: 'platform.organisation.sso', label: 'SSO' },
          { id: 'platform.organisation.advanced', label: 'Advanced (Audit log)' },
        ],
      },
      { id: 'platform.documents', label: 'Documents' },
      { id: 'platform.settings', label: 'Settings' },
      { id: 'platform.billing', label: 'Billing' },
      { id: 'platform.plugins', label: 'Plugins' },
      { id: 'platform.api', label: 'API / MCP' },
    ],
  },
  {
    id: 'approver',
    label: 'Approver',
  },
];

/**
 * Convert selected permission IDs to Better Auth role string (comma-separated)
 */
export function permissionsToRoles(permissionIds: string[]): string {
  const roles = new Set<string>();

  permissionIds.forEach((permissionId) => {
    const role = PERMISSION_TO_ROLE_MAP[permissionId];
    if (role) {
      roles.add(role);
    }
  });

  return Array.from(roles).sort().join(',');
}

/**
 * Convert Better Auth role string (comma-separated) to permission IDs
 */
export function rolesToPermissions(rolesString: string): string[] {
  if (!rolesString) return [];

  const roles = rolesString.split(',').map((r) => r.trim());
  const permissionIds = new Set<string>();

  roles.forEach((role) => {
    const permissions = ROLE_TO_PERMISSION_MAP[role];
    if (permissions) {
      permissions.forEach((p) => permissionIds.add(p));
    }
  });

  return Array.from(permissionIds).sort();
}

/**
 * Check if a user has permission to grant a specific permission
 * Rule: Users can only grant permissions they have themselves
 */
export function canGrantPermission(userRoles: string, permissionToGrant: string): boolean {
  const userPermissions = rolesToPermissions(userRoles);

  // Admin can grant any permission
  if (userPermissions.includes('admin')) {
    return true;
  }

  // User must have the permission to grant it
  return userPermissions.includes(permissionToGrant);
}

/**
 * Filter permissions tree based on user's current permissions
 * Only show permissions the user can grant
 */
export function filterPermissionsByUserRole(
  permissions: TreeNode[],
  userRoles: string
): TreeNode[] {
  const userPermissions = rolesToPermissions(userRoles);

  // Admin sees everything
  if (userPermissions.includes('admin')) {
    return permissions;
  }

  // Filter tree recursively
  function filterNode(node: TreeNode): TreeNode | null {
    // Check if user has this permission
    if (!userPermissions.includes(node.id)) {
      return null;
    }

    // If node has children, filter them too
    if (node.children) {
      const filteredChildren = node.children
        .map((child) => filterNode(child))
        .filter((child): child is TreeNode => child !== null);

      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
    }

    return node;
  }

  return permissions
    .map((node) => filterNode(node))
    .filter((node): node is TreeNode => node !== null);
}
